import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react'
import { MoreHorizontal, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Textarea } from '@/components/ui/textarea'
import { Typography } from '@/components/ui/typography'
import { OGSM_COPY } from '@/features/ogsm/ogsm-labels'
import { cn } from '@/lib/utils'

interface InlineTextProps {
  value: string | null | undefined
  /**
   * Called with the trimmed text, or null when the field was emptied. The
   * editor closes when it resolves and stays open with the text when it rejects.
   */
  onSave: (value: string | null) => Promise<unknown>
  /** Called when the edit is discarded (Cancel or Escape). */
  onCancel?: () => void
  /** Accessible name of the editor. */
  label: string
  /** Label of the button offered while the field is empty and editable. */
  placeholder: string
  /** Accessible name of the "···" menu beside a saved value. */
  menuLabel: string
  /** The menu item that opens the editor. */
  editLabel: string
  disabled?: boolean
  /** How the saved value reads at rest. */
  children?: (value: string) => ReactNode
  className?: string
}

/**
 * A textarea with Done and Cancel; clicking away keeps the draft open. An
 * empty value offers a button; a saved one opens from its "···" menu or on
 * double-click. It keeps no copy of the saved value: the parent passes it
 * from the board.
 */
export function InlineText({
  value,
  onSave,
  onCancel,
  label,
  placeholder,
  menuLabel,
  editLabel,
  disabled = false,
  children,
  className,
}: InlineTextProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  // Guards a second Done or Ctrl+Enter before `saving` re-renders.
  const inFlight = useRef(false)
  const trigger = useRef<HTMLElement | null>(null)
  const refocus = useRef(false)
  // The menu's item swaps the trigger for the editor: keep focus in the textarea.
  const editChosen = useRef(false)
  // Whether the last full click was on a control inside the text (Show more).
  const controlClicked = useRef(false)
  const open = !disabled && editing

  useEffect(() => {
    if (!open && refocus.current) {
      refocus.current = false
      trigger.current?.focus()
    }
  }, [open])

  function start() {
    setDraft(value ?? '')
    setEditing(true)
  }

  function close() {
    refocus.current = true
    setEditing(false)
  }

  function cancel() {
    close()
    onCancel?.()
  }

  async function done() {
    if (inFlight.current) return
    const next = draft.trim()
    if (next === (value ?? '')) {
      close()
      return
    }
    inFlight.current = true
    setSaving(true)
    try {
      await onSave(next === '' ? null : next)
      close()
    } catch {
      // The parent shows the refusal; the draft stays for another try.
    } finally {
      inFlight.current = false
      setSaving(false)
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Escape') {
      event.preventDefault()
      cancel()
    } else if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      void done()
    }
  }

  if (open) {
    return (
      <div className={cn('flex flex-col gap-2', className)}>
        <Textarea
          aria-label={label}
          autoFocus
          rows={3}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={onKeyDown}
          className="w-full border-primary text-sm"
        />
        <div className="flex gap-2">
          <Button size="sm" disabled={saving} onClick={() => void done()}>
            {OGSM_COPY.done}
          </Button>
          <Button variant="outline" size="sm" onClick={cancel}>
            {OGSM_COPY.cancel}
          </Button>
        </div>
      </div>
    )
  }

  if (!value) {
    return disabled ? null : (
      <Button
        ref={(element) => {
          trigger.current = element
        }}
        variant="ghost"
        size="sm"
        className={cn('self-start text-primary/60', className)}
        onClick={start}
      >
        <Plus className="size-3" />
        {placeholder}
      </Button>
    )
  }

  function noteClick(event: MouseEvent<HTMLDivElement>) {
    // The second click of a double-click keeps the first one's verdict: the
    // control may have moved away from under it.
    if (event.detail > 1) return
    controlClicked.current =
      event.target instanceof Element && event.target.closest('button') !== null
  }

  function startFromText() {
    const fromControl = controlClicked.current
    controlClicked.current = false
    if (!fromControl) start()
  }

  return (
    <div className={cn('flex items-start gap-2', className)}>
      <div
        className={cn('min-w-0 flex-1', !disabled && 'cursor-text')}
        onClick={disabled ? undefined : noteClick}
        onDoubleClick={disabled ? undefined : startFromText}
      >
        {children ? (
          children(value)
        ) : (
          <Typography variant="body">{value}</Typography>
        )}
      </div>
      {!disabled && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              ref={(element) => {
                trigger.current = element
              }}
              variant="ghost"
              size="icon-xs"
              aria-label={menuLabel}
              className="shrink-0"
            >
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            onCloseAutoFocus={(event) => {
              if (editChosen.current) event.preventDefault()
              editChosen.current = false
            }}
          >
            <DropdownMenuItem
              onSelect={() => {
                editChosen.current = true
                start()
              }}
            >
              {editLabel}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}
